import type { ReactNode } from "react";
import { Check, CircleCheck, CircleX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { inputClass, textareaClass } from "../ui";
import type { AnswerMap, AnswerValue, QuestionFeedback, QuizQuestion } from "../../types";

export interface QuestionProps {
  question: QuizQuestion;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  answers: AnswerMap;
  onAnswerChild?: (child: QuizQuestion, value: AnswerValue) => void;
  // Set once a quiz is submitted: marks keyed by question id (scenario
  // children included). Answers stay editable; a change starts a new attempt.
  feedback?: Record<string, QuestionFeedback>;
}

type Mark = "correct" | "wrong" | undefined;

// How one option reads after submission: only the teacher's own picks are
// marked, green when right and red when wrong. The right answer for a wrong
// pick is spelled out under the question instead.
function optionMark(
  feedback: QuestionFeedback | undefined,
  optionID: string,
  selected: boolean
): Mark {
  if (!feedback || !selected) return undefined;
  return feedback.correct_option_ids.includes(optionID) ? "correct" : "wrong";
}

function Fieldset({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className="min-w-0 border-0 p-0">
      <legend className="mb-3 p-0 text-base font-semibold leading-snug text-app-ink">
        {legend}
      </legend>
      <div className="flex flex-col gap-2">{children}</div>
    </fieldset>
  );
}

// One answer as a tappable card. The native input stays in the DOM (hidden)
// so keyboard and screen-reader behaviour of radios and checkboxes is kept.
function OptionCard({
  kind,
  name,
  selected,
  onSelect,
  mark,
  children,
}: {
  kind: "radio" | "checkbox";
  name?: string;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  mark?: Mark;
  children: ReactNode;
}) {
  // Marked cards stay tappable: changing an answer starts a new attempt.
  const MarkIcon = mark === "correct" ? CircleCheck : CircleX;
  const tone =
    mark === "correct"
      ? "bg-success-bg text-success-text ring-1 ring-success-text/40"
      : mark === "wrong"
        ? "bg-danger-bg text-danger ring-1 ring-danger/40"
        : selected
          ? "bg-accent-soft text-app-ink shadow-raised"
          : "bg-surface text-app-ink shadow-sm ring-1 ring-app-line";
  return (
    <label
      className={`flex cursor-pointer select-none items-start gap-3 rounded-xl p-3.5 transition-all focus-within:ring-2 focus-within:ring-accent/40 active:scale-[0.99] ${tone}`}
    >
      <input
        className="sr-only"
        type={kind}
        name={name}
        checked={selected}
        onChange={(event) => onSelect(event.target.checked)}
      />
      {mark ? (
        <MarkIcon size={20} className="shrink-0" aria-hidden="true" />
      ) : (
        <span
          className={`mt-0.5 grid size-5 shrink-0 place-items-center ${
            kind === "radio" ? "rounded-full" : "rounded-md"
          } ${selected ? "bg-accent text-white" : "bg-app-field"}`}
          aria-hidden="true"
        >
          {selected &&
            (kind === "radio" ? (
              <span className="size-2 rounded-full bg-white" />
            ) : (
              <Check size={14} strokeWidth={3} />
            ))}
        </span>
      )}
      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold leading-snug">
        {children}
      </span>
    </label>
  );
}

// The verdict under a submitted question: right or wrong, the correct
// answer and, for a wrong answer, why it is correct — in the app's language
// when the portal has a translation, or a placeholder until the explanation
// is written.
function FeedbackNote({
  question,
  feedback,
}: {
  question: QuizQuestion;
  feedback: QuestionFeedback;
}) {
  const { t, i18n } = useTranslation();
  // Open (typed) answers are marked by hand and have no verdict to show yet.
  if (feedback.correct === null) return null;
  const reason =
    feedback.explanation_translations[i18n.language] ||
    feedback.explanation ||
    t("quizExplanationPlaceholder");
  const correctAnswer = question.options
    .filter((option) => feedback.correct_option_ids.includes(option.id))
    .map((option) => option.text)
    .join(", ");
  // A right pick is already marked green on its option card; the note would
  // only repeat it. Types without option cards still need the verdict.
  if (feedback.correct && correctAnswer) return null;
  const VerdictIcon = feedback.correct ? CircleCheck : CircleX;
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-xl p-3 ${feedback.correct ? "bg-success-bg" : "bg-danger-bg"}`}
    >
      <p
        className={`flex items-center gap-1.5 text-sm font-semibold ${feedback.correct ? "text-success-text" : "text-danger"}`}
      >
        <VerdictIcon size={16} className="shrink-0" aria-hidden="true" />
        {t(feedback.correct ? "quizCorrectLabel" : "quizIncorrect")}
      </p>
      {correctAnswer && (
        <p className="text-sm leading-snug text-app-copy">
          <strong className="font-semibold text-success-text">{t("quizCorrectAnswer")}</strong>{" "}
          {correctAnswer}
        </p>
      )}
      {/* Only a wrong answer is explained, in its own box under the correct option. */}
      {!feedback.correct && (
        <div className="mt-1 flex flex-col gap-1 rounded-lg bg-surface p-3 ring-1 ring-app-line">
          <strong className="text-sm font-semibold text-app-ink">{t("quizExplanation")}</strong>
          <p className="whitespace-pre-line text-sm leading-snug text-app-copy">{reason}</p>
        </div>
      )}
    </div>
  );
}

// Question renders all 13 quiz question types and the section heading. Answer keys are never sent
// to the client before submission (server strips them), so these renderers
// only ever see safe, ungraded data until feedback arrives with the result.
export function Question(props: QuestionProps) {
  if (!props.feedback) return <QuestionBody {...props} />;
  const own = props.feedback[props.question.id];
  return (
    <div className="flex flex-col gap-3">
      <QuestionBody {...props} />
      {own && <FeedbackNote question={props.question} feedback={own} />}
    </div>
  );
}

function QuestionBody({
  question,
  value,
  onChange,
  answers,
  onAnswerChild,
  feedback,
}: QuestionProps) {
  const name = `q-${question.id}`;
  const own = feedback?.[question.id];

  if (["single_choice", "true_false", "image_based"].includes(question.type)) {
    return (
      <Fieldset legend={question.text}>
        {question.options.map((option) => (
          <OptionCard
            key={option.id}
            kind="radio"
            name={name}
            selected={value === option.id}
            onSelect={() => onChange(option.id)}
            mark={optionMark(own, option.id, value === option.id)}
          >
            {option.image_url && (
              <img src={option.image_url} alt="" className="h-12 w-12 rounded object-cover" />
            )}
            {option.text}
          </OptionCard>
        ))}
      </Fieldset>
    );
  }

  if (question.type === "multiple_choice") {
    const selected = (value as string[] | undefined) || [];
    return (
      <Fieldset legend={question.text}>
        {question.options.map((option) => (
          <OptionCard
            key={option.id}
            kind="checkbox"
            selected={selected.includes(option.id)}
            mark={optionMark(own, option.id, selected.includes(option.id))}
            onSelect={(checked) =>
              onChange(
                checked ? [...selected, option.id] : selected.filter((id) => id !== option.id)
              )
            }
          >
            {option.text}
          </OptionCard>
        ))}
      </Fieldset>
    );
  }

  if (question.type === "matching" || question.type === "drag_drop") {
    const left = question.match_pairs?.left_items || [];
    const right = question.match_pairs?.right_items || [];
    const assignments = (value as Record<string, string> | undefined) || {};
    return (
      <Fieldset legend={question.text}>
        {left.map((leftItem) => (
          <div key={leftItem.id} className="my-2.5 grid gap-1.5">
            <span className="text-sm font-medium text-app-ink">{leftItem.text}</span>
            <select
              className={inputClass}
              value={assignments[leftItem.id] || ""}
              onChange={(event) => onChange({ ...assignments, [leftItem.id]: event.target.value })}
            >
              <option value="">Select a match</option>
              {right.map((rightItem, index) => (
                <option key={index} value={rightItem.text}>
                  {rightItem.text}
                </option>
              ))}
            </select>
          </div>
        ))}
      </Fieldset>
    );
  }

  if (question.type === "ordering") {
    const orderItems = question.order_items || [];
    const items = (value as string[] | undefined) || orderItems.map((entry) => entry.id);
    const labelByID = Object.fromEntries(orderItems.map((entry) => [entry.id, entry.text]));

    function move(from: number, to: number) {
      if (to < 0 || to >= items.length) return;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      onChange(next);
    }

    return (
      <Fieldset legend={question.text}>
        <ol className="grid gap-2">
          {items.map((id, index) => (
            <li
              key={id}
              className="flex items-center justify-between gap-2 rounded-md border border-app-line p-2 text-sm"
            >
              <span>{labelByID[id]}</span>
              <span className="flex gap-1">
                <button
                  type="button"
                  className="px-2 py-1 text-app-copy disabled:opacity-30"
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                  aria-label="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="px-2 py-1 text-app-copy disabled:opacity-30"
                  disabled={index === items.length - 1}
                  onClick={() => move(index, index + 1)}
                  aria-label="Move down"
                >
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ol>
      </Fieldset>
    );
  }

  if (question.type === "fill_blank" || question.type === "dropdown_blank") {
    const blanks = question.blanks || [];
    const filled = (value as Record<string, string> | undefined) || {};
    return (
      <Fieldset legend={question.text}>
        {blanks.map((blank) => (
          <label key={blank.id} className="my-2 grid gap-1.5 text-sm font-medium text-app-ink">
            Blank {blank.blank_index}
            {question.type === "fill_blank" ? (
              <input
                className={inputClass}
                value={filled[String(blank.blank_index)] || ""}
                onChange={(event) =>
                  onChange({ ...filled, [String(blank.blank_index)]: event.target.value })
                }
              />
            ) : (
              <select
                className={inputClass}
                value={filled[String(blank.blank_index)] || ""}
                onChange={(event) =>
                  onChange({ ...filled, [String(blank.blank_index)]: event.target.value })
                }
              >
                <option value="">Select</option>
                {(blank.options || []).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.text}
                  </option>
                ))}
              </select>
            )}
          </label>
        ))}
      </Fieldset>
    );
  }

  if (question.type === "section") {
    return (
      <h3 className="whitespace-pre-line border-b border-app-line pb-2 pt-1 text-lg font-semibold leading-snug text-app-ink">
        {question.text}
      </h3>
    );
  }

  if (question.type === "scenario") {
    return (
      <fieldset className="grid gap-4 rounded-md border border-app-line p-4">
        <legend className="px-1 text-sm font-semibold text-app-ink">Scenario</legend>
        <p className="text-sm leading-relaxed text-app-ink">{question.text}</p>
        {(question.children || []).map((child) => (
          <Question
            key={child.id}
            question={child}
            value={answers[child.id]}
            onChange={(childValue) => onAnswerChild?.(child, childValue)}
            answers={answers}
            onAnswerChild={onAnswerChild}
            feedback={feedback}
          />
        ))}
      </fieldset>
    );
  }

  return (
    <label className="grid gap-1.5 text-sm font-semibold text-app-ink">
      {question.text}
      {question.type === "long_text" ? (
        <textarea
          className={textareaClass}
          value={(value as string | undefined) || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          type={question.type === "number" ? "number" : "text"}
          value={(value as string | number | undefined) ?? ""}
          onChange={(event) =>
            onChange(question.type === "number" ? Number(event.target.value) : event.target.value)
          }
        />
      )}
    </label>
  );
}
